import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { SlideUpLine, FadeUp, Stagger, StaggerItem } from "@/pages/_shared/anim";
import { Link } from "react-router-dom";

const steps = [
  {
    code: "01",
    title: "Requirement",
    desc: "Tell us about your elevator, escalator, spare part, installation requirement, or technical issue.",
    duration: "Share details",
  },
  {
    code: "02",
    title: "Technical Assessment",
    desc: "Our team reviews the available system information and identifies possible technical approaches.",
    duration: "We evaluate",
  },
  {
    code: "03",
    title: "Solution & Quotation",
    desc: "Receive the recommended part, service, repair, upgrade, or installation solution with commercial details.",
    duration: "Proposal stage",
  },
  {
    code: "04",
    title: "Supply / Service / Installation",
    desc: "Depending on the requirement, we coordinate supply, repair, installation, programming, technical service, or commissioning.",
    duration: "Execution",
  },
  {
    code: "05",
    title: "Testing & Support",
    desc: "Where applicable, testing, commissioning, inspection, and technical follow-up help complete the solution.",
    duration: "Follow-up",
  },
];

export default function Process() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % steps.length);
    }, 4000);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <section id="process" className="bg-white py-20 md:py-28">
      <div className="px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <FadeUp>
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-crest-deep/60 uppercase">
                <span className="w-6 h-px bg-crest-deep/40" /> How we work
              </span>
            </FadeUp>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-crest-deep leading-tight mt-5">
              <SlideUpLine as="div">A Clear Process</SlideUpLine>
              <SlideUpLine as="div" delay={0.16}>
                <span className="text-crest-bg">for Complex</span> Requirements.
              </SlideUpLine>
            </h2>
          </div>
          <FadeUp delay={0.2} className="lg:col-span-7 lg:pt-6 text-crest-deep/70 text-base md:text-lg leading-relaxed">
            From the first requirement to the final sign-off, we follow a predictable five-phase process designed to match the technical complexity of elevator and escalator systems.
          </FadeUp>
        </div>

        <div
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
        <Stagger className="space-y-4" stagger={0.08}>
          {steps.map((s, i) => {
            const isActive = i === activeIdx;
            return (
              <StaggerItem key={s.code}>
                <motion.div
                  onMouseEnter={() => setActiveIdx(i)}
                  animate={{
                    backgroundColor: isActive
                      ? "rgb(245, 243, 238)"
                      : "rgba(255,255,255,0)",
                    borderColor: isActive
                      ? "rgb(234, 117, 38)"
                      : "rgba(0,0,0,0.05)",
                  }}
                  transition={{ duration: 0.35 }}
                  className="relative grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-center py-6 md:py-7 px-4 md:px-6 rounded-lg border cursor-pointer overflow-hidden"
                >
                  <motion.div
                    animate={{
                      color: isActive
                        ? "rgb(15,27,46)"
                        : "rgba(26,45,66,0.5)",
                    }}
                    transition={{ duration: 0.3 }}
                    className="md:col-span-1 font-display text-3xl md:text-4xl"
                  >
                    {s.code}
                  </motion.div>
                  <div className="md:col-span-3">
                    <h3 className="font-display text-2xl md:text-3xl text-crest-deep">
                      {s.title}
                    </h3>
                  </div>
                  <div className="md:col-span-6 text-crest-deep/70 text-sm md:text-base leading-relaxed">
                    {s.desc}
                  </div>
                  <div className="md:col-span-2 flex items-center md:justify-end gap-2 text-xs md:text-sm font-semibold text-crest-deep">
                    <span className="w-2 h-2 rounded-full bg-crest-orange" />
                    <span className="whitespace-nowrap">{s.duration}</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-transparent">
                    <motion.div
                      key={`prog-${i}-${activeIdx}-${paused}`}
                      initial={{ width: "0%" }}
                      animate={{
                        width: isActive && !paused ? "100%" : "0%",
                      }}
                      transition={{
                        duration: isActive && !paused ? 4 : 0.3,
                        ease: "linear",
                      }}
                      className="h-full bg-crest-orange"
                    />
                  </div>
                </motion.div>
              </StaggerItem>
            );
          })}
        </Stagger>
        </div>

        <FadeUp delay={0.1} className="mt-12 flex justify-center">
          <Link
            to="/process"
            className="inline-flex items-center gap-2 border border-crest-deep/30 text-crest-deep hover:bg-crest-deep hover:text-crest-orange hover:border-crest-deep transition-colors px-7 py-3.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            SEE OUR PROCESS
            <i className="ri-arrow-right-up-line"></i>
          </Link>
        </FadeUp>
      </div>
    </section>
  );
}